import { useState, useCallback, type ReactElement } from 'react'
import { ScanInstructionsScreen } from './ScanInstructionsScreen.js'
import { useStep } from '../state/useStep.js'
import { useDemoMode } from '../state/useDemoMode.js'
import { useDemoStore } from '../state/store.js'
import { LiveCameraScan } from '../live/LiveCameraScan.js'
import { DemoVideoScan } from '../live/DemoVideoScan.js'
import { SCAN_CONFIGS } from '../live/scans.js'
import { useGrantLifetime, type CameraGrant } from '../live/useRequestCameraGrant.js'

const CONFIG = SCAN_CONFIGS['model-label']

export const ScanModelLabelScreen = (): ReactElement => {
  const { goto } = useStep()
  const { dispatch } = useDemoStore()
  const [phase, setPhase] = useState<'instructions' | 'scanning'>('instructions')
  const [grant, setGrant] = useState<CameraGrant | null>(null)
  const demo = useDemoMode()

  useGrantLifetime(grant)

  const onComplete = useCallback((findings: unknown) => {
    dispatch({
      type: 'MODEL_LABEL_DETECTED',
      findings: findings as { brand: string; model: string; rawLabelText: string },
    })
    goto('model-lookup')
  }, [dispatch, goto])

  const onSkipOrAbort = useCallback(() => {
    // Manual entry fallback: skip ahead with the FALLBACK_MODEL.
    dispatch({
      type: 'MODEL_LABEL_DETECTED',
      findings: { brand: 'Generic', model: 'Generic 24" Built-In', rawLabelText: '' },
    })
    goto('model-lookup')
  }, [dispatch, goto])

  const onProceed = useCallback((g: CameraGrant | null) => {
    // Clear any stale live-patch from a prior run so the chip rail starts fresh.
    dispatch({ type: 'CLEAR_LIVE_PATCH', scan: 'model-label' })
    if (g) setGrant(g)
    setPhase('scanning')
  }, [dispatch])

  if (phase === 'instructions') {
    return (
      <ScanInstructionsScreen
        title="Scan instructions"
        backTo="appliance-status"
        headline="Point at the model label"
        body="Open your dishwasher door and point the camera at the inside-door sticker. We'll read the brand and model number for you."
        tip="Most labels are on the inside of the door, near the top."
        ctaLabel="Open Camera"
        hero={<div className="border-2 border-dashed border-primary rounded-xl w-28 h-32" />}
        onProceed={onProceed}
        onSkip={onSkipOrAbort}
      />
    )
  }

  if (demo) {
    return (
      <DemoVideoScan
        scanConfig={CONFIG}
        onComplete={onComplete}
        onAbort={onSkipOrAbort}
      />
    )
  }

  if (!grant) {
    // Live mode entered phase=scanning without a grant — shouldn't happen
    // (onProceed only sets phase after setting grant), but fall back gracefully.
    return null as unknown as ReactElement
  }
  return <LiveCameraScan scanConfig={CONFIG} grant={grant} onComplete={onComplete} onAbort={onSkipOrAbort} />
}
